import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { LooseRelationship, TableMetadata } from '../../../../../types/electron';
import { buildForeignKeyMap, lookupForeignKey } from '../../../table-explorer/hooks/useTableMetadata';
import { normalizeColumnName } from '../../utils/normalizeColumnName';

/**
 * Resolved real-FK target for one source-column in a result set. Mirrors the
 * shape of `ColumnForeignKeyInfo` (Table Explorer's hook), narrowed to the
 * fields the cell editor needs.
 */
export type ResultSetRealFkTarget = {
  schema: string;
  table: string;
  column: string;
};

type ColumnSource = {
  sourceSchema?: string;
  sourceTable?: string;
  sourceColumn?: string;
};

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * An unambiguous key for a tuple of names: a delimited name may hold any
 * separator (`orders."vendor|ref"` next to `"orders|vendor".ref`).
 */
function sourceKey(...parts: readonly string[]): string {
  return JSON.stringify(parts);
}

/**
 * A result column's key in `useResultSetRealFkMap`: its source triple, the
 * column as the result names it (exact — `"ref id"` and `"ref-id"` are two
 * keys); null without a source column.
 */
export function resultSetFkKey(column: ColumnSource): string | null {
  if (!column.sourceTable || !column.sourceColumn) return null;
  return sourceKey(column.sourceSchema ?? '', column.sourceTable, column.sourceColumn);
}

type SourceTable = { schema: string; table: string };

/**
 * Fetches `getTableMetadata` once per unique source table in a result set.
 * Shared by every hook in this file so a grid that needs several facets of
 * the same metadata (FK targets, native-enum flags, …) never fans out extra
 * IPC — all observers land on the same `useTableMetadata` cache entry.
 */
function useSourceTableMetadata({
  connectionId,
  database,
  columns,
}: {
  connectionId: string | null;
  database: string | null;
  columns: readonly ColumnSource[];
}): { sources: SourceTable[]; metadata: (TableMetadata | undefined)[] } {
  const sources = useMemo(() => {
    const seen = new Set<string>();
    const list: { schema: string; table: string }[] = [];
    for (const col of columns) {
      if (!col.sourceTable) continue;
      const key = sourceKey(col.sourceSchema ?? '', col.sourceTable);
      if (seen.has(key)) continue;
      seen.add(key);
      list.push({ schema: col.sourceSchema ?? '', table: col.sourceTable });
    }
    return list;
  }, [columns]);

  const queries = useQueries({
    queries: sources.map((s) => ({
      // Same queryKey shape as `useTableMetadata` so both hooks share the cache.
      queryKey: ['table-metadata', connectionId, database, s.schema, s.table] as const,
      queryFn: async (): Promise<TableMetadata | undefined> => {
        if (!connectionId || !database || !window.electronAPI?.getTableMetadata) return undefined;
        const result = await window.electronAPI.getTableMetadata({
          connectionId,
          database,
          schema: s.schema,
          tableName: s.table,
        });
        return result.success ? result.metadata : undefined;
      },
      enabled: !!connectionId && !!database,
      staleTime: FIVE_MINUTES,
      refetchOnWindowFocus: false,
    })),
  });

  return { sources, metadata: queries.map((q) => q.data) };
}

/**
 * Resolves real foreign-key targets for every column in a Query Editor result
 * set by fetching `getTableMetadata` for each unique source table. Cell
 * editing on real FK columns needs this because QE result columns carry
 * `sourceSchema/sourceTable/sourceColumn` but no inline FK info — the FK
 * shape lives on the source table's constraints.
 *
 * Returns a Map keyed by `resultSetFkKey` (the source schema, table and column,
 * the source column exactly as the result names it) so the caller can resolve
 * any result column in O(1); each is read from its source table's map by
 * `lookupForeignKey` — its exact name, else the one table column it names
 * ignoring case — so the cell editor never takes another column's FK. Loose
 * FKs are handled separately by the caller (they live in MetaInfo, not in
 * table metadata), but `looseRelationships` decides PRECEDENCE: the map is
 * built by Table Explorer's own `buildForeignKeyMap` (Two Results Grids
 * parity), so a single-column real FK stays while a column that is only a
 * member of a composite FK yields to a declaration on it and is left out
 * (`realFkOutranksLoose`).
 */
export function useResultSetRealFkMap(args: {
  connectionId: string | null;
  database: string | null;
  columns: readonly ColumnSource[];
  /** The database's MetaInfo loose declarations (`DatabaseMetaInfo.looseRelationships`). */
  looseRelationships?: readonly LooseRelationship[];
}): Map<string, ResultSetRealFkTarget> {
  const { sources, metadata } = useSourceTableMetadata(args);
  const { looseRelationships, columns } = args;

  return useMemo(() => {
    const fkMapByTable = new Map<string, ReturnType<typeof buildForeignKeyMap>>();
    metadata.forEach((meta, i) => {
      const source = sources[i];
      if (!meta || !source) return;
      const tableLoose = (looseRelationships ?? []).filter(
        (r) => r.fromSchema === source.schema && r.fromTable === source.table,
      );
      const columnNames = (meta.columns ?? []).map((c) => c.name);
      fkMapByTable.set(sourceKey(source.schema, source.table), buildForeignKeyMap(meta.constraints, tableLoose, columnNames));
    });
    const map = new Map<string, ResultSetRealFkTarget>();
    for (const column of columns) {
      const key = resultSetFkKey(column);
      if (!key || map.has(key)) continue;
      const tableMap = fkMapByTable.get(sourceKey(column.sourceSchema ?? '', column.sourceTable!));
      const info = lookupForeignKey(tableMap, column.sourceColumn!);
      if (!info || info.isLoose) continue;
      map.set(key, {
        schema: info.referencedSchema ?? '',
        table: info.referencedTable,
        column: info.referencedColumn,
      });
    }
    return map;
    // `metadata` is a fresh array every render — spread its entries so the memo
    // recomputes only when a table's metadata actually arrives/changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, columns, looseRelationships, ...metadata]);
}

/**
 * Result-set columns whose SOURCE column has a native enum type in its engine.
 * Returns the grid column ids (not source keys) so callers can gate per
 * rendered column in O(1).
 *
 * Why this exists: Postgres resolves a result column's `dataType` through
 * `format_type`, which yields the enum's own type name (`mpaa_rating`), never
 * `USER-DEFINED` — so a PG native enum is undetectable from the result
 * column's type string alone. The source table's metadata carries the real
 * signal (`pg_type.typtype = 'e'` → `ColumnMetadata.isEnum`); Table Explorer
 * already reads it via `useTableMetadata`, and this hook is the Query Editor's
 * equivalent, sharing the very same query cache.
 */
export function useResultSetNativeEnumColumnIds({
  connectionId,
  database,
  columns,
}: {
  connectionId: string | null;
  database: string | null;
  columns: readonly (ColumnSource & { id: string })[];
}): Set<string> {
  const { sources, metadata } = useSourceTableMetadata({ connectionId, database, columns });

  return useMemo(() => {
    // (schema, table, normalized column) → native-enum.
    const nativeEnumKeys = new Set<string>();
    metadata.forEach((meta, i) => {
      const source = sources[i];
      if (!meta || !source) return;
      for (const col of meta.columns ?? []) {
        if (col.isEnum !== true) continue;
        nativeEnumKeys.add(sourceKey(source.schema, source.table, normalizeColumnName(col.name)));
      }
    });

    const ids = new Set<string>();
    for (const column of columns) {
      if (!column.sourceTable || !column.sourceColumn) continue;
      const key = sourceKey(column.sourceSchema ?? '', column.sourceTable, normalizeColumnName(column.sourceColumn));
      if (nativeEnumKeys.has(key)) ids.add(column.id);
    }
    return ids;
    // `metadata` is a fresh array every render — spread its entries so the memo
    // recomputes only when a table's metadata actually arrives/changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, sources, ...metadata]);
}
